import type {
  Lead,
  LeadActivity,
  Pipeline,
  PipelineStage,
  Prisma,
  PrismaClient,
} from '@prisma/client';
import Decimal from 'decimal.js';
import { z } from 'zod';
import { normalisePhone } from '@roovers/shared';
import { HttpError } from '../../core/errors.js';
import { cancelJobs, enqueueJob, registerJobHandler } from '../../core/jobs.js';
import { permissionsGranted } from '../auth/auth.middleware.js';
import type { AuthContext } from '../auth/auth.types.js';
import { createParty, partyInputSchema } from '../parties/parties.service.js';
import { isoOf, saveDraft, toDate } from '../sales/documents.service.js';

export type Tx = Prisma.TransactionClient;
export type Db = PrismaClient | Tx;

export const LEAD_SOURCES = [
  'website',
  'referral',
  'whatsapp',
  'walk_in',
  'social',
  'cold_outreach',
  'other',
] as const;
export const SERVICE_INTERESTS = ['software', 'hosting', 'equipment', 'consultancy'] as const;
export const ACTIVITY_TYPES = ['note', 'call', 'meeting', 'email', 'whatsapp'] as const;

// ───────────────────────── pipelines ─────────────────────────

const DEFAULT_PIPELINES: { name: string; stages: [string, number, 'open' | 'won' | 'lost'][] }[] = [
  {
    name: 'Software Projects',
    stages: [
      ['New', 10, 'open'],
      ['Contacted', 20, 'open'],
      ['Requirements', 40, 'open'],
      ['Proposal sent', 60, 'open'],
      ['Negotiation', 80, 'open'],
      ['Won', 100, 'won'],
      ['Lost', 0, 'lost'],
    ],
  },
  {
    name: 'Hosting & Domains',
    stages: [
      ['New', 15, 'open'],
      ['Quoted', 50, 'open'],
      ['Won', 100, 'won'],
      ['Lost', 0, 'lost'],
    ],
  },
  {
    name: 'Equipment Sales',
    stages: [
      ['New', 10, 'open'],
      ['Quoted', 40, 'open'],
      ['Negotiation', 70, 'open'],
      ['Won', 100, 'won'],
      ['Lost', 0, 'lost'],
    ],
  },
  {
    name: 'Consultancy',
    stages: [
      ['New', 10, 'open'],
      ['Discovery call', 30, 'open'],
      ['Proposal sent', 60, 'open'],
      ['Won', 100, 'won'],
      ['Lost', 0, 'lost'],
    ],
  },
];

/** First use: four pipelines that match the business's service lines. The owner can edit all of it. */
export async function ensurePipelines(db: Db, companyId: number) {
  if ((await db.pipeline.count({ where: { companyId } })) > 0) return;
  let pos = 0;
  for (const p of DEFAULT_PIPELINES) {
    await db.pipeline.create({
      data: {
        companyId,
        name: p.name,
        isDefault: pos === 0,
        position: pos++,
        stages: {
          create: p.stages.map(([name, probability, kind], i) => ({
            name,
            probability,
            kind,
            position: i,
          })),
        },
      },
    });
  }
}

export const pipelineInputSchema = z.object({
  name: z.string().trim().min(2).max(100),
  isDefault: z.boolean().optional(),
  active: z.boolean().optional(),
  stages: z
    .array(
      z.object({
        id: z.number().int().positive().optional(),
        name: z.string().trim().min(1).max(100),
        probability: z.number().int().min(0).max(100),
        kind: z.enum(['open', 'won', 'lost']).default('open'),
      }),
    )
    .min(3, 'A pipeline needs at least three stages')
    .max(20),
});
export type PipelineInput = z.infer<typeof pipelineInputSchema>;

export type PipelineWithStages = Pipeline & { stages: PipelineStage[] };
export const pipelineInclude = { stages: { orderBy: { position: 'asc' as const } } };

export function assertStagesValid(stages: PipelineInput['stages']) {
  if (!stages.some((s) => s.kind === 'won'))
    throw new HttpError(400, 'VALIDATION_ERROR', 'Add a "won" stage');
  if (!stages.some((s) => s.kind === 'lost'))
    throw new HttpError(400, 'VALIDATION_ERROR', 'Add a "lost" stage');
  const names = stages.map((s) => s.name.toLowerCase());
  if (new Set(names).size !== names.length)
    throw new HttpError(400, 'VALIDATION_ERROR', 'Stage names must be different');
  for (const s of stages) {
    if (s.kind === 'won' && s.probability !== 100)
      throw new HttpError(400, 'VALIDATION_ERROR', 'A won stage has probability 100');
    if (s.kind === 'lost' && s.probability !== 0)
      throw new HttpError(400, 'VALIDATION_ERROR', 'A lost stage has probability 0');
  }
}

export async function savePipeline(
  tx: Tx,
  companyId: number,
  input: PipelineInput,
  id?: number,
): Promise<PipelineWithStages> {
  assertStagesValid(input.stages);
  const dup = await tx.pipeline.findFirst({
    where: { companyId, name: input.name, ...(id ? { id: { not: id } } : {}) },
  });
  if (dup) throw new HttpError(409, 'DUPLICATE', 'A pipeline with this name already exists');
  if (input.isDefault)
    await tx.pipeline.updateMany({ where: { companyId }, data: { isDefault: false } });
  let pipeline: Pipeline;
  if (id) {
    const existing = await tx.pipeline.findFirst({
      where: { id, companyId },
      include: { stages: true },
    });
    if (!existing) throw new HttpError(404, 'NOT_FOUND', 'Pipeline not found');
    pipeline = await tx.pipeline.update({
      where: { id },
      data: {
        name: input.name,
        ...(input.isDefault !== undefined ? { isDefault: input.isDefault } : {}),
        ...(input.active !== undefined ? { active: input.active } : {}),
      },
    });
    // Stages that still hold leads cannot be removed.
    const keepIds = new Set(input.stages.map((s) => s.id).filter(Boolean));
    for (const old of existing.stages.filter((s) => !keepIds.has(s.id))) {
      const used = await tx.lead.count({ where: { stageId: old.id } });
      if (used > 0)
        throw new HttpError(
          409,
          'STAGE_IN_USE',
          `Stage "${old.name}" still has ${used} lead(s). Move them first.`,
        );
      await tx.pipelineStage.delete({ where: { id: old.id } });
    }
    for (const [i, s] of input.stages.entries()) {
      if (s.id) {
        const cur = existing.stages.find((x) => x.id === s.id);
        if (!cur) throw new HttpError(400, 'VALIDATION_ERROR', 'Unknown stage');
        if (cur.kind !== s.kind && (await tx.lead.count({ where: { stageId: cur.id } })) > 0)
          throw new HttpError(
            409,
            'STAGE_IN_USE',
            `Stage "${cur.name}" has leads, so its type cannot change.`,
          );
        await tx.pipelineStage.update({
          where: { id: s.id },
          data: { name: s.name, probability: s.probability, kind: s.kind, position: i },
        });
      } else {
        await tx.pipelineStage.create({
          data: {
            pipelineId: id,
            name: s.name,
            probability: s.probability,
            kind: s.kind,
            position: i,
          },
        });
      }
    }
  } else {
    const count = await tx.pipeline.count({ where: { companyId } });
    pipeline = await tx.pipeline.create({
      data: {
        companyId,
        name: input.name,
        isDefault: input.isDefault ?? count === 0,
        position: count,
        stages: {
          create: input.stages.map((s, i) => ({
            name: s.name,
            probability: s.probability,
            kind: s.kind,
            position: i,
          })),
        },
      },
    });
  }
  return tx.pipeline.findUniqueOrThrow({ where: { id: pipeline.id }, include: pipelineInclude });
}

// ───────────────────────── scoring ─────────────────────────

export interface ScoreRule {
  id: string;
  label: string;
  field: 'hasEmail' | 'hasPhone' | 'source' | 'serviceInterest' | 'valueAtLeast';
  equals?: string;
  value?: number;
  points: number;
}
export const DEFAULT_SCORE_RULES: ScoreRule[] = [
  { id: 'email', label: 'Has an email', field: 'hasEmail', points: 5 },
  { id: 'phone', label: 'Has a phone number', field: 'hasPhone', points: 5 },
  { id: 'referral', label: 'Came by referral', field: 'source', equals: 'referral', points: 20 },
  {
    id: 'website',
    label: 'Filled the website form',
    field: 'source',
    equals: 'website',
    points: 10,
  },
  {
    id: 'v5m',
    label: 'Estimated value 5,000,000 or more (UGX)',
    field: 'valueAtLeast',
    value: 5_000_000,
    points: 20,
  },
  {
    id: 'v1m',
    label: 'Estimated value 1,000,000 or more (UGX)',
    field: 'valueAtLeast',
    value: 1_000_000,
    points: 10,
  },
];
export const scoreRulesSchema = z
  .array(
    z.object({
      id: z.string().min(1).max(30),
      label: z.string().min(1).max(100),
      field: z.enum(['hasEmail', 'hasPhone', 'source', 'serviceInterest', 'valueAtLeast']),
      equals: z.string().max(30).optional(),
      value: z.number().min(0).optional(),
      points: z.number().int().min(-100).max(100),
    }),
  )
  .max(30);

export async function getScoreRules(db: Db, companyId: number): Promise<ScoreRule[]> {
  const row = await db.setting.findUnique({
    where: { companyId_key: { companyId, key: 'leadScoring' } },
  });
  return (row?.value as ScoreRule[] | undefined) ?? DEFAULT_SCORE_RULES;
}

export function computeScore(
  lead: {
    email: string | null;
    phone: string | null;
    source: string;
    serviceInterest: string | null;
    estimatedValue: Decimal.Value;
    currency: string;
    rate?: Decimal.Value;
  },
  rules: ScoreRule[],
): number {
  const baseValue = new Decimal(lead.estimatedValue.toString()).times((lead.rate ?? 1).toString());
  let score = 0;
  for (const r of rules) {
    const hit =
      r.field === 'hasEmail'
        ? !!lead.email
        : r.field === 'hasPhone'
          ? !!lead.phone
          : r.field === 'source'
            ? lead.source === r.equals
            : r.field === 'serviceInterest'
              ? lead.serviceInterest === r.equals
              : baseValue.gte(r.value ?? 0);
    if (hit) score += r.points;
  }
  return Math.max(0, score);
}

// ───────────────────────── leads ─────────────────────────

const money = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .refine((v) => /^\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid amount');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

export const leadInputSchema = z.object({
  name: z.string().trim().min(2, 'Enter the lead or company name').max(200),
  contactName: z.string().trim().max(200).nullable().optional(),
  phone: z.string().trim().max(30).nullable().optional(),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email('Enter a valid email')
    .max(320)
    .nullable()
    .optional()
    .or(z.literal('').transform(() => null)),
  source: z.enum(LEAD_SOURCES).default('other'),
  serviceInterest: z.enum(SERVICE_INTERESTS).nullable().optional(),
  estimatedValue: money.default('0'),
  currency: z
    .string()
    .trim()
    .length(3)
    .transform((v) => v.toUpperCase())
    .default('UGX'),
  ownerId: z.number().int().positive().nullable().optional(),
  pipelineId: z.number().int().positive().optional(),
  stageId: z.number().int().positive().optional(),
  tags: z.array(z.string().trim().min(1).max(30)).max(10).optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
  nextFollowUpDate: isoDate.nullable().optional(),
});
export type LeadInput = z.infer<typeof leadInputSchema>;

export type LeadWithStage = Lead;

export const ownScope = (auth: AuthContext): Prisma.LeadWhereInput =>
  auth.scope === 'own' ? { ownerId: auth.userId } : {};

export async function loadLead(db: Db, auth: AuthContext, id: number): Promise<Lead> {
  const l = await db.lead.findFirst({
    where: { id, companyId: auth.companyId, ...ownScope(auth) },
  });
  if (!l) throw new HttpError(404, 'NOT_FOUND', 'Lead not found');
  return l;
}

/** Possible duplicates among open leads and existing clients (same email, phone or a very similar name). */
export async function findLeadDuplicates(
  db: Db,
  companyId: number,
  probe: { name?: string | null; email?: string | null; phone?: string | null },
  excludeId?: number,
) {
  const phone = probe.phone ? normalisePhone(probe.phone) : null;
  const or: Prisma.LeadWhereInput[] = [];
  if (probe.email) or.push({ email: probe.email.toLowerCase() });
  if (phone) or.push({ phone });
  if (probe.name && probe.name.trim().length >= 3)
    or.push({ name: { contains: probe.name.trim() } });
  const leads = or.length
    ? await db.lead.findMany({
        where: { companyId, OR: or, ...(excludeId ? { id: { not: excludeId } } : {}) },
        select: { id: true, name: true, email: true, phone: true, status: true },
        take: 5,
      })
    : [];
  const partyOr: Prisma.PartyWhereInput[] = [];
  if (probe.name && probe.name.trim().length >= 3)
    partyOr.push({ name: { contains: probe.name.trim() } });
  if (probe.email) partyOr.push({ contacts: { some: { email: probe.email.toLowerCase() } } });
  if (phone) partyOr.push({ contacts: { some: { phone } } });
  const parties = partyOr.length
    ? await db.party.findMany({
        where: { companyId, OR: partyOr },
        select: { id: true, name: true, isClient: true },
        take: 5,
      })
    : [];
  return { leads, parties };
}

async function resolveStage(tx: Db, companyId: number, pipelineId?: number, stageId?: number) {
  await ensurePipelines(tx, companyId);
  const pipeline = pipelineId
    ? await tx.pipeline.findFirst({
        where: { id: pipelineId, companyId, active: true },
        include: pipelineInclude,
      })
    : ((await tx.pipeline.findFirst({
        where: { companyId, isDefault: true, active: true },
        include: pipelineInclude,
      })) ??
      (await tx.pipeline.findFirst({
        where: { companyId, active: true },
        include: pipelineInclude,
        orderBy: { position: 'asc' },
      })));
  if (!pipeline) throw new HttpError(400, 'VALIDATION_ERROR', 'Pipeline not found');
  const stage = stageId
    ? pipeline.stages.find((s) => s.id === stageId)
    : pipeline.stages.find((s) => s.kind === 'open');
  if (!stage)
    throw new HttpError(400, 'VALIDATION_ERROR', 'Stage does not belong to this pipeline');
  return { pipeline, stage };
}

export async function logActivity(
  db: Db,
  leadId: number,
  type: string,
  note: string | null,
  userId: number | null,
) {
  return db.leadActivity.create({
    data: { leadId, type, note: note?.slice(0, 1000) ?? null, userId },
  });
}

async function assertOwner(tx: Db, auth: AuthContext, ownerId: number | null | undefined) {
  if (!ownerId || ownerId === auth.userId) return;
  if (auth.scope === 'own')
    throw new HttpError(403, 'FORBIDDEN', 'You can only assign leads to yourself');
  const u = await tx.user.findFirst({
    where: { id: ownerId, companyId: auth.companyId, status: 'active' },
  });
  if (!u) throw new HttpError(400, 'VALIDATION_ERROR', 'Choose an active team member as owner');
}

export async function createLead(
  tx: Tx,
  auth: AuthContext,
  input: LeadInput,
  opts: { website?: { detail?: string }; ownerFallback?: number | null } = {},
): Promise<Lead> {
  const { pipeline, stage } = await resolveStage(
    tx,
    auth.companyId,
    input.pipelineId,
    input.stageId,
  );
  const ownerId = input.ownerId ?? opts.ownerFallback ?? auth.userId;
  await assertOwner(tx, auth, ownerId);
  const phone = input.phone ? normalisePhone(input.phone) : null;
  if (input.phone && !phone)
    throw new HttpError(
      400,
      'VALIDATION_ERROR',
      'Enter a valid phone number, for example 0772123456',
    );
  const rate = await currencyRate(tx, auth.companyId, input.currency);
  const score = computeScore(
    {
      ...input,
      email: input.email ?? null,
      phone,
      serviceInterest: input.serviceInterest ?? null,
      rate,
    },
    await getScoreRules(tx, auth.companyId),
  );
  const lead = await tx.lead.create({
    data: {
      companyId: auth.companyId,
      name: input.name,
      contactName: input.contactName ?? null,
      phone,
      email: input.email ?? null,
      source: input.source,
      serviceInterest: input.serviceInterest ?? null,
      estimatedValue: input.estimatedValue,
      currency: input.currency,
      ownerId,
      pipelineId: pipeline.id,
      stageId: stage.id,
      status: stage.kind === 'open' ? 'open' : stage.kind,
      tags: input.tags ?? [],
      notes: input.notes ?? null,
      nextFollowUpDate: input.nextFollowUpDate ? toDate(input.nextFollowUpDate) : null,
      score,
      sourceDetail: opts.website?.detail ?? null,
      needsReview: !!opts.website,
      createdBy: auth.userId,
    },
  });
  await tx.leadStageHistory.create({
    data: { leadId: lead.id, toStageId: stage.id, changedBy: auth.userId },
  });
  await logActivity(
    tx,
    lead.id,
    opts.website ? 'website' : 'created',
    opts.website
      ? `Submitted through the website form${opts.website.detail ? ` (${opts.website.detail})` : ''}`
      : 'Lead created',
    opts.website ? null : auth.userId,
  );
  return lead;
}

async function currencyRate(db: Db, companyId: number, currency: string): Promise<Decimal> {
  if (currency === 'UGX') return new Decimal(1);
  const r = await db.exchangeRate.findFirst({
    where: { companyId, currency },
    orderBy: { effectiveDate: 'desc' },
  });
  return r ? new Decimal(r.rate.toString()) : new Decimal(1);
}

export async function updateLead(
  tx: Tx,
  auth: AuthContext,
  id: number,
  input: Partial<LeadInput>,
): Promise<Lead> {
  const lead = await loadLead(tx, auth, id);
  if (input.ownerId !== undefined) await assertOwner(tx, auth, input.ownerId);
  const phone =
    input.phone === undefined ? undefined : input.phone ? normalisePhone(input.phone) : null;
  if (input.phone && !phone)
    throw new HttpError(
      400,
      'VALIDATION_ERROR',
      'Enter a valid phone number, for example 0772123456',
    );
  const merged = {
    email: input.email === undefined ? lead.email : (input.email ?? null),
    phone: phone === undefined ? lead.phone : phone,
    source: input.source ?? lead.source,
    serviceInterest:
      input.serviceInterest === undefined ? lead.serviceInterest : (input.serviceInterest ?? null),
    estimatedValue: input.estimatedValue ?? lead.estimatedValue.toString(),
    currency: input.currency ?? lead.currency,
  };
  const score = computeScore(
    { ...merged, rate: await currencyRate(tx, auth.companyId, merged.currency) },
    await getScoreRules(tx, auth.companyId),
  );
  const updated = await tx.lead.update({
    where: { id },
    data: {
      ...(input.name !== undefined ? { name: input.name } : {}),
      ...(input.contactName !== undefined ? { contactName: input.contactName } : {}),
      phone: merged.phone,
      email: merged.email,
      source: merged.source,
      serviceInterest: merged.serviceInterest,
      estimatedValue: merged.estimatedValue,
      currency: merged.currency,
      ...(input.ownerId !== undefined ? { ownerId: input.ownerId } : {}),
      ...(input.tags !== undefined ? { tags: input.tags } : {}),
      ...(input.notes !== undefined ? { notes: input.notes } : {}),
      ...(input.nextFollowUpDate !== undefined
        ? { nextFollowUpDate: input.nextFollowUpDate ? toDate(input.nextFollowUpDate) : null }
        : {}),
      score,
      needsReview: false,
      updatedBy: auth.userId,
    },
  });
  if (input.ownerId !== undefined && input.ownerId !== lead.ownerId)
    await logActivity(tx, id, 'note', `Assigned to a new owner`, auth.userId);
  return updated;
}

export async function moveLead(
  tx: Tx,
  auth: AuthContext,
  id: number,
  stageId: number,
  lostReason?: string,
): Promise<Lead> {
  const lead = await loadLead(tx, auth, id);
  const stage = await tx.pipelineStage.findFirst({
    where: { id: stageId, pipelineId: lead.pipelineId },
  });
  if (!stage)
    throw new HttpError(400, 'VALIDATION_ERROR', "That stage is not in this lead's pipeline");
  if (stage.kind === 'lost' && (!lostReason || lostReason.trim().length < 3))
    throw new HttpError(422, 'LOST_REASON_REQUIRED', 'Say why the lead was lost');
  if (stage.id === lead.stageId) return lead;
  const from = await tx.pipelineStage.findUnique({ where: { id: lead.stageId } });
  const updated = await tx.lead.update({
    where: { id },
    data: {
      stageId: stage.id,
      status: stage.kind === 'open' ? 'open' : stage.kind,
      lostReason: stage.kind === 'lost' ? lostReason!.trim() : null,
      closedAt: stage.kind === 'open' ? null : new Date(),
      ...(stage.kind !== 'open' ? { nextFollowUpDate: null } : {}),
      updatedBy: auth.userId,
    },
  });
  await tx.leadStageHistory.create({
    data: { leadId: id, fromStageId: lead.stageId, toStageId: stage.id, changedBy: auth.userId },
  });
  await logActivity(
    tx,
    id,
    'stage_change',
    `${from?.name ?? '?'} → ${stage.name}${stage.kind === 'lost' ? ` (${lostReason!.trim()})` : ''}`,
    auth.userId,
  );
  return updated;
}

// ───────────────────────── conversion ─────────────────────────

export async function convertLead(
  tx: Tx,
  auth: AuthContext,
  id: number,
  opts: { createQuotation: boolean; partyId?: number },
): Promise<{ lead: Lead; partyId: number; quotationId: number | null }> {
  const lead = await loadLead(tx, auth, id);
  if (lead.status === 'lost')
    throw new HttpError(409, 'INVALID_STATE', 'Reopen the lead before converting it');
  let partyId = opts.partyId ?? lead.partyId;
  if (partyId) {
    const p = await tx.party.findFirst({ where: { id: partyId, companyId: auth.companyId } });
    if (!p) throw new HttpError(400, 'VALIDATION_ERROR', 'Client not found');
    if (!p.isClient)
      await tx.party.update({ where: { id: p.id }, data: { isClient: true, isProspect: false } });
  } else {
    // Same creation path as "+ Create client" in place: name is enough, flagged Incomplete for review.
    const party = await createParty(tx, {
      companyId: auth.companyId,
      userId: auth.userId,
      needsReview: true,
      input: partyInputSchema.parse({
        name: lead.name,
        isClient: true,
        ...(lead.contactName || lead.email || lead.phone
          ? {
              contacts: [
                {
                  name: lead.contactName ?? lead.name,
                  email: lead.email ?? undefined,
                  phone: lead.phone ?? undefined,
                  isPrimary: true,
                  receivesDocuments: !!lead.email,
                },
              ],
            }
          : {}),
      }),
    });
    partyId = party.id;
  }
  let quotationId: number | null = lead.quotationId;
  if (opts.createQuotation && !quotationId) {
    const interest = lead.serviceInterest
      ? lead.serviceInterest[0]!.toUpperCase() + lead.serviceInterest.slice(1)
      : 'Services';
    const revenueKey =
      {
        software: 'REV_SOFTWARE',
        hosting: 'REV_HOSTING',
        equipment: 'REV_EQUIP',
        consultancy: 'REV_CONSULT',
      }[lead.serviceInterest ?? ''] ?? 'REV_OTHER';
    const q = await saveDraft(tx, {
      auth,
      kind: 'quotation',
      input: {
        partyId,
        currency: lead.currency,
        issueDate: new Date().toISOString().slice(0, 10),
        reference: `Lead #${lead.id}`,
        notes: lead.notes ?? undefined,
        lines: [
          {
            kind: 'item',
            description: `${interest}: ${lead.name}`,
            quantity: '1',
            unitPrice: lead.estimatedValue.toString(),
            discountType: 'none',
            discountValue: '0',
            vatCode: 'VAT_STD',
            isOptional: false,
            isSelected: true,
            whtApplicable: false,
            revenueAccountKey: revenueKey as never,
            saveToCatalogue: false,
          },
        ],
      },
    });
    quotationId = q.id;
  }
  const updated = await tx.lead.update({
    where: { id },
    data: { partyId, quotationId, needsReview: false, updatedBy: auth.userId },
  });
  await logActivity(
    tx,
    id,
    'conversion',
    quotationId ? 'Converted to a client with a draft quotation' : 'Converted to a client',
    auth.userId,
  );
  return { lead: updated, partyId, quotationId };
}

// ───────────────────────── follow-ups on the shared reminder engine ─────────────────────────

export async function ensureLeadRule(db: Db, companyId: number) {
  const existing = await db.reminderRule.findFirst({ where: { companyId, docType: 'lead' } });
  if (existing) return existing;
  return db.reminderRule.create({
    data: {
      companyId,
      docType: 'lead',
      name: 'Lead follow-up date',
      trigger: 'follow_up',
      offsetDays: 0,
      audience: 'owner',
      templateName: 'reminder',
      active: true,
    },
  });
}

const followKey = (leadId: number) => `rem:lead:${leadId}:`;

/** One follow-up job per lead date; changing the date cancels the old one. Fires at 08:00 Africa/Kampala. */
export async function scheduleLeadFollowUp(db: PrismaClient, lead: Lead): Promise<void> {
  await cancelJobs(db, followKey(lead.id));
  await db.scheduledReminder.updateMany({
    where: { docType: 'lead', docId: lead.id, status: 'pending' },
    data: { status: 'cancelled' },
  });
  if (!lead.nextFollowUpDate || lead.status !== 'open') return;
  const rule = await ensureLeadRule(db, lead.companyId);
  if (!rule.active) return;
  const d = lead.nextFollowUpDate;
  const runAt = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 8 - 3, 0, 0),
  );
  const row = await db.scheduledReminder.upsert({
    where: { docId_ruleId: { docId: lead.id, ruleId: rule.id } },
    create: { companyId: lead.companyId, ruleId: rule.id, docType: 'lead', docId: lead.id, runAt },
    update: { status: 'pending', runAt, firedAt: null, note: null },
  });
  await db.job.deleteMany({ where: { idempotencyKey: `${followKey(lead.id)}${isoOf(d)}` } });
  await enqueueJob(db, {
    companyId: lead.companyId,
    type: 'lead.followup',
    payload: { scheduledId: row.id, leadId: lead.id, date: isoOf(d) },
    runAt,
    idempotencyKey: `${followKey(lead.id)}${isoOf(d)}`,
  });
}

export function registerCrmJobs() {
  registerJobHandler('lead.followup', async (job, ctx) => {
    const { scheduledId, leadId, date } = job.payload as {
      scheduledId: number;
      leadId: number;
      date: string;
    };
    const db = ctx.db;
    const row = await db.scheduledReminder.findUnique({ where: { id: scheduledId } });
    if (!row || row.status !== 'pending') return;
    const lead = await db.lead.findUnique({ where: { id: leadId } });
    // Conditions are re-checked when it fires: still open, and the date has not been moved.
    if (
      !lead ||
      lead.status !== 'open' ||
      !lead.nextFollowUpDate ||
      isoOf(lead.nextFollowUpDate) !== date
    ) {
      await db.scheduledReminder.update({
        where: { id: row.id },
        data: {
          status: 'skipped',
          firedAt: ctx.now,
          note: 'Lead closed or follow-up date changed',
        },
      });
      return;
    }
    if (lead.ownerId) {
      await db.task.create({
        data: {
          companyId: lead.companyId,
          assigneeId: lead.ownerId,
          title: `Follow up with ${lead.name}`,
          description: lead.contactName
            ? `Contact: ${lead.contactName}${lead.phone ? ` (${lead.phone})` : ''}`
            : null,
          dueDate: lead.nextFollowUpDate,
          relatedType: 'lead',
          relatedId: lead.id,
          source: 'reminder',
        },
      });
      await db.notification.create({
        data: {
          companyId: lead.companyId,
          userId: lead.ownerId,
          title: `Follow-up due: ${lead.name}`,
          link: `/crm/leads/${lead.id}`,
        },
      });
    }
    await logActivity(db, lead.id, 'task', 'Follow-up reminder sent to the owner', null);
    await db.scheduledReminder.update({
      where: { id: row.id },
      data: { status: 'fired', firedAt: ctx.now },
    });
  });
}

// ───────────────────────── views ─────────────────────────

export const leadView = (
  l: Lead,
  extra: {
    ownerName?: string | null;
    stageName?: string;
    pipelineName?: string;
    stageKind?: string;
    probability?: number;
  } = {},
) => ({
  id: l.id,
  name: l.name,
  contactName: l.contactName,
  phone: l.phone,
  email: l.email,
  source: l.source,
  serviceInterest: l.serviceInterest,
  estimatedValue: l.estimatedValue.toFixed(4),
  currency: l.currency,
  ownerId: l.ownerId,
  ownerName: extra.ownerName ?? null,
  pipelineId: l.pipelineId,
  pipelineName: extra.pipelineName,
  stageId: l.stageId,
  stageName: extra.stageName,
  stageKind: extra.stageKind,
  probability: extra.probability ?? 0,
  status: l.status,
  lostReason: l.lostReason,
  tags: (l.tags as string[] | null) ?? [],
  notes: l.notes,
  nextFollowUpDate: isoOf(l.nextFollowUpDate),
  score: l.score,
  partyId: l.partyId,
  quotationId: l.quotationId,
  sourceDetail: l.sourceDetail,
  needsReview: l.needsReview,
  closedAt: l.closedAt,
  createdAt: l.createdAt,
  updatedAt: l.updatedAt,
});

export const activityView = (a: LeadActivity, userName?: string | null) => ({
  id: a.id,
  type: a.type,
  note: a.note,
  by: userName ?? null,
  at: a.createdAt,
});

export const can = (auth: AuthContext, p: string) => permissionsGranted(auth.permissions, p);

/** Safety net (runs with the cron sweep): imported or bulk-edited leads get their follow-up scheduled. */
export async function scheduleMissingLeadFollowUps(db: PrismaClient): Promise<number> {
  const leads = await db.lead.findMany({
    where: { status: 'open', nextFollowUpDate: { not: null } },
    take: 500,
  });
  let n = 0;
  for (const l of leads) {
    const pending = await db.scheduledReminder.count({
      where: { docType: 'lead', docId: l.id, status: 'pending' },
    });
    const fired = await db.scheduledReminder.count({
      where: {
        docType: 'lead',
        docId: l.id,
        status: { in: ['fired', 'skipped'] },
        runAt: { gte: l.nextFollowUpDate! },
      },
    });
    if (pending === 0 && fired === 0) {
      await scheduleLeadFollowUp(db, l);
      n += 1;
    }
  }
  return n;
}
